import * as signalR from '@microsoft/signalr';
import { getCompanyFromHost } from '@utils/url';

const defaultApiUrl = `${import.meta.env.VITE_APP_API}/notificationhub?subdomain=${getCompanyFromHost()}`;

export class SignalRConnection {
  connection: signalR.HubConnection | null = null;

  events: Map<string, (notification: any) => void>;

  apiUrl: string;

  connectionState: number = 0;

  token?: string;

  constructor(apiUrl = defaultApiUrl) {
    this.events = new Map();
    this.apiUrl = apiUrl;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.token && this.connectionState === 1) {
        this.connect(this.token);
      }
    });
  }

  /**
   * @summary Connects to the SignalR hub and listens for notifications.
   * @param {string} token - The access token for the user.
   */
  connect = async (token: string) => {
    if (!this.connection) {
      this.token = token;
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(this.apiUrl, {
          accessTokenFactory: () => token,
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .build();
      try {
        await this.connection.start();
        this.connectionState = 2;
        this.connection.on('onDisconnect', () => (this.connectionState = 1));
        this.connection.on('onclose', () => (this.connectionState = 1));
        this.events.forEach((value, key) => this.connection?.on(key.split('::')[0], value));
      } catch (error: any) {
        this.connection = null;
      }
    }
  };

  /**
   * @summary Disconnects from the SignalR hub.
   * @returns {void}
   */
  disconnect = () => {
    this.connection?.stop();
    this.connection = null;
    this.connectionState = 0;
    this.events.clear();
    this.token = undefined;
  };

  /**
   * Registers an event handler for the specified method.
   *
   * @param method - The name of the event method to register.
   * @param endpoint - The endpoint to listen to.
   * @param fn - The event handler to invoke when the event is received.
   */
  registerEvent = (method: string, endpoint: string, fn: (notification: any) => void) => {
    const methodKey = `${method}::${endpoint}`;
    const methodFn = this.events.get(methodKey);
    if (methodFn) {
      this.connection?.off(method, methodFn);
    }
    this.events.set(methodKey, fn);
    this.connection?.on(method, fn);
  };

  /**
   * Unregister an event handler for the specified method.
   *
   * @param method - The name of the event method to unregister.
   * @param endpoint - The endpoint to listen to.
   */
  unregisterEvent = (method: string, endpoint: string) => {
    const methodKey = `${method}::${endpoint}`;
    const methodFn = this.events.get(methodKey);
    if (methodFn) {
      this.connection?.off(method, methodFn);
      this.events.delete(methodKey);
    }
  };
}

export const rdBackSignalr = new SignalRConnection();
